// La BIBLIOTECA de adjuntos: la columna de la derecha de la pantalla del Agente desde la
// que se engancha DE UN TOQUE lo que el chat sabe adjuntar —productos, modelos, poses,
// fondos, creaciones e imágenes ya subidas— sin pasar por un selector modal
// (ADR-2026-09-12-la-biblioteca-de-adjuntos-en-la-pantalla-del-agente).
//
// Sólo en DESKTOP y sólo en la pantalla dedicada: en el panel lateral (420px) y en
// mobile no hay ancho para dos columnas, y ahí las cards del "+" siguen abriendo los
// selectores de siempre (`SelectoresDeAdjuntos`). Acá las mismas cards abren esta
// columna en su pestaña.
//
// Se abre SÓLO desde una card del "+" y se cierra con su propia "X": el botón de
// biblioteca del encabezado de la pantalla se fue
// (ADR-2026-10-08-la-biblioteca-sin-boton-en-el-encabezado).
//
// Tocar una miniatura la adjunta y SE VA de la grilla volando hasta su chip en la caja
// (`useVueloALaCaja`, el gesto del Catálogo); lo que está adjunto no se muestra acá
// (`yaAdjunto`, por id estable) y vuelve a la grilla cuando se saca el chip. Modelos y
// poses son fotos verticales: sus cards van en 3:4; el resto, cuadradas. Productos y modelos
// vienen del entorno del chat (ya cargados); poses (catálogo + las propias), fondos,
// creaciones y subidas se piden la primera vez que se abre su pestaña y quedan mientras
// el panel viva.
//
// Cada pestaña también AGREGA (ADR-2026-09-12-la-biblioteca-agrega-en-todas-las-
// pestanas): la grilla arranca con una tarjeta de acción por alta —la misma tarjeta que
// encabeza las grillas de los selectores— que abre la pantalla de alta correspondiente
// (`AltaDeBiblioteca`, que monta las MISMAS pantallas que usan los modos). Creaciones no
// agrega: se generan desde el chat. "Subir imagen" no tiene pantalla: abre el file
// picker y sube por el mismo camino que el "+" de la caja.

import { SkuDelProducto } from '../../components/SkuDelProducto';
import { useEffect, useRef, useState } from 'react';
import { Plus, Search, X } from 'lucide-react';
import CachedImg from '../../components/CachedImg';
import { AccionCard } from '../../pages/v2/components/AccionCard';
import { useChatAtlas } from '../ChatAtlasContext';
import { useEntornoChatAtlas } from '../entornoContext';
import {
  archivosEnganchados,
  esArchivoDelUsuario,
  lugarLibreAdjuntos,
  mensajeLimiteAdjuntos,
} from '../adjuntosChatAtlas';
import { prepararAdjunto } from './prepararAdjunto';
import { type ChatAttachment } from '../../pages/v2/agentAttachments';
import { uploadCustomPose } from '../../pages/v2/posesCustomApi';
import {
  ALTAS_POR_PESTANA,
  PESTANAS_BIBLIOTECA,
  pestanasVisibles,
  adjuntoDeFondoGuardado,
  adjuntoDeModelo,
  yaAdjunto,
  type PestanaBiblioteca,
} from './bibliotecaDeAdjuntos';
// La carga de cada fuente y sus fichas viven en el hook, compartido con el selector de
// la caja de la home (ADR-2026-09-21-la-caja-de-la-home-abre-el-selector-de-fuentes).
import { useFuentesDeAdjuntos, type FichaDeFuente } from './useFuentesDeAdjuntos';
import { AltaDeBiblioteca, type AltaConPantalla } from './AltaDeBiblioteca';
import { BibliotecaMoodboards } from './BibliotecaMoodboards';
import { prefiereMenosMovimiento, useVueloALaCaja } from './vueloAlComposer';
import { CajaEspejoDeAdjuntos } from './CajaEspejoDeAdjuntos';
import { ATRIBUTO_FICHA, useReacomodoSuave } from './reacomodoDeGrilla';

interface Props {
  pestana: PestanaBiblioteca;
  /** Ancho en píxeles (lo calcula la pantalla con `anchoDeBiblioteca`): la apertura
   *  anima el ancho del contenedor de afuera y esta columna se queda a su medida. */
  ancho: number;
  onCambiarPestana: (pestana: PestanaBiblioteca) => void;
  onCerrar: () => void;
  /**
   * Dibujar al pie la caja ESPEJO con los chips del chat. Sólo cuando la biblioteca se
   * abre ENCIMA del chat (modo lienzo): ahí la caja real queda tapada y la miniatura que
   * vuela al adjuntar necesita a dónde llegar
   * (ADR-2026-09-16-con-el-lienzo-la-biblioteca-espeja-la-caja).
   */
  espejoDeAdjuntos?: boolean;
}

const BOTON_CERRAR =
  'inline-flex h-9 w-9 items-center justify-center rounded-full text-[#02062B]/[0.6] transition-colors hover:bg-[#02062B]/[0.05] hover:text-brand-black';

export const PanelBiblioteca: React.FC<Props> = ({
  pestana,
  ancho,
  onCambiarPestana,
  onCerrar,
  espejoDeAdjuntos = false,
}) => {
  const chat = useChatAtlas();
  // Todo del ENTORNO, marca incluida — ADR-2026-09-13-el-cambio-de-marca-no-llega-al-chat.
  // Antes este panel tenía DOS marcas activas a la vez: `brandId` del entorno (leído una
  // sola vez al montar, así que quedaba en la marca anterior al cambiar) para el alta de
  // producto, y el `marcaId` de `useRubroMarcaActiva` para los moodboards. Un producto dado
  // de alta acá después de cambiar de marca caía en la marca vieja. Ahora el provider
  // resuelve la marca con ese mismo hook y de acá sale una sola.
  //
  // El rubro define las fotos de ejemplo del alta de producto y qué pestañas se ven
  // (Moodboards es sólo de Moda).
  // (Los productos y los modelos del entorno los lee el hook de fuentes, que es el que
  // arma las fichas de cada pestaña.)
  const {
    companyId,
    usuarioId,
    brandId: marcaId,
    rubro,
    marcaResuelta,
    plan,
    refrescarModelos,
    refrescarProductos,
  } = useEntornoChatAtlas();
  const pestanas = pestanasVisibles(rubro);
  // Si la pestaña guardada no existe para esta marca (se cambió de marca con la
  // biblioteca en Moodboards), se vuelve a la primera.
  useEffect(() => {
    if (marcaResuelta && !pestanas.some((p) => p.id === pestana)) onCambiarPestana('productos');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [marcaResuelta, rubro, pestana]);
  const [busqueda, setBusqueda] = useState('');
  const [aviso, setAviso] = useState<string | null>(null);
  const { despegar } = useVueloALaCaja();
  // Cuando una card se va a la caja (o vuelve), las demás se deslizan a su lugar.
  const grillaRef = useRef<HTMLDivElement | null>(null);
  useReacomodoSuave(grillaRef);
  // Qué alta está abierta (null = la grilla).
  const [alta, setAlta] = useState<AltaConPantalla | null>(null);
  const [subiendoPose, setSubiendoPose] = useState(false);
  const [subiendoImagenes, setSubiendoImagenes] = useState(false);
  const inputImagenesRef = useRef<HTMLInputElement | null>(null);
  // Qué muestra la pestaña abierta: lo pide el hook (perezoso y cacheado) y lo devuelve
  // ya convertido en fichas con su adjunto.
  const { fichas, cargando, refrescarPosesPropias, refrescarFondosGuardados, refrescarSubidas } =
    useFuentesDeAdjuntos(pestana);

  // Cambiar de pestaña limpia el buscador, el aviso y el alta: son de la grilla que se va.
  useEffect(() => {
    setBusqueda('');
    setAviso(null);
    setAlta(null);
  }, [pestana]);

  // Creaciones y subidas no tienen nombre: no hay qué buscar. Moodboards son pocas
  // carpetas: tampoco.
  const conNombres = pestana !== 'creaciones' && pestana !== 'subidas' && pestana !== 'moodboards';
  const filtro = busqueda.trim().toLowerCase();
  // Lo adjunto no está en la grilla: se mudó a la caja. Vuelve al sacar el chip.
  const disponibles = fichas.filter((f) => !yaAdjunto(chat.adjuntos, f.adjunto));
  const visibles = filtro
    ? disponibles.filter((f) => (f.nombre ?? '').toLowerCase().includes(filtro))
    : disponibles;
  const pestanaActual = PESTANAS_BIBLIOTECA.find((p) => p.id === pestana) ?? PESTANAS_BIBLIOTECA[0];
  // Las tarjetas de alta no se filtran con el buscador: agregar siempre está a mano.
  const altas = ALTAS_POR_PESTANA[pestana];

  const enganchar = (adjunto: ChatAttachment): boolean => {
    // Una creación o una subida es un ARCHIVO del mensaje y cuenta para el tope del
    // "+"; un producto, un modelo o una referencia del catálogo, no.
    if (esArchivoDelUsuario(adjunto) && lugarLibreAdjuntos(archivosEnganchados(chat.adjuntos)) === 0) {
      setAviso(mensajeLimiteAdjuntos());
      return false;
    }
    chat.agregarAdjunto(adjunto);
    setAviso(null);
    return true;
  };

  // Tocar una miniatura: se mide ANTES de adjuntar (después ya no está en el DOM) y el
  // vuelo se despega en el mismo handler que el adjunto, para que caigan en un solo
  // commit (ver `useVueloALaCaja`).
  const tomar = (ficha: Pick<FichaDeFuente, 'adjunto' | 'imageUrl'>, miniatura: HTMLElement | null) => {
    const vuelo =
      miniatura && !prefiereMenosMovimiento()
        ? {
            id: ficha.adjunto.id,
            origen: miniatura.getBoundingClientRect(),
            src: ficha.imageUrl,
            radio: getComputedStyle(miniatura).borderRadius,
          }
        : null;
    if (!enganchar(ficha.adjunto)) return;
    if (vuelo) despegar(vuelo);
  };

  // Modelos y poses son fotos de cuerpo: en cuadrado se cortan. Las tarjetas de alta
  // toman la misma medida para que la grilla quede pareja.
  const aspecto = pestana === 'modelos' || pestana === 'poses' ? 'aspect-[3/4]' : 'aspect-square';

  // Subir una pose: el mismo camino que el catálogo de los modos (`uploadCustomPose`
  // con una persona, y después refrescar "Mis poses"). El error se muestra en el panel
  // con el mismo texto que allá.
  const subirPose = async (archivo: File) => {
    if (!companyId || !usuarioId) {
      setAviso('No pudimos identificar tu cuenta. Probá de nuevo en un momento.');
      return;
    }
    setSubiendoPose(true);
    setAviso(null);
    try {
      await uploadCustomPose({
        userId: usuarioId,
        idCompany: companyId,
        file: archivo,
        cantidadPersonas: 1,
      });
      await refrescarPosesPropias();
    } catch (e) {
      console.error('[PanelBiblioteca] subir pose', e);
      setAviso('No pudimos procesar la pose. Probá de nuevo con otra foto.');
    } finally {
      setSubiendoPose(false);
    }
  };

  // Subir imágenes: el mismo camino que el "+" de la caja (`prepararAdjunto`, que las
  // sube a `agent-refs`), con el mismo tope y los mismos mensajes. Después se refresca
  // "Subidas", donde ya aparecen.
  const subirImagenes = async (archivos: File[]) => {
    if (archivos.length === 0) return;
    const libre = lugarLibreAdjuntos(archivosEnganchados(chat.adjuntos));
    if (libre === 0) {
      setAviso(mensajeLimiteAdjuntos());
      return;
    }
    const aProcesar = archivos.slice(0, libre);
    const sobraron = archivos.length > libre;
    setSubiendoImagenes(true);
    let fallo: string | null = null;
    for (const archivo of aProcesar) {
      try {
        chat.agregarAdjunto(await prepararAdjunto(archivo, companyId));
      } catch (err) {
        // Se guarda el PRIMER motivo y se sigue con el resto, como en la caja.
        fallo = fallo ?? (err instanceof Error ? err.message : 'No pude leer ese archivo.');
      }
    }
    setSubiendoImagenes(false);
    setAviso(fallo ?? (sobraron ? mensajeLimiteAdjuntos() : null));
    void refrescarSubidas();
  };

  const cabecera = (
    <header className="flex h-14 shrink-0 items-center gap-1 border-b border-[#02062B]/[0.06] px-3">
      <p className="min-w-0 flex-1 truncate px-1 text-[14px] font-semibold text-brand-black">Biblioteca</p>
      <button type="button" onClick={onCerrar} aria-label="Cerrar la biblioteca" className={BOTON_CERRAR}>
        <X className="h-5 w-5" />
      </button>
    </header>
  );

  return (
    <aside
      aria-label="Biblioteca de adjuntos"
      // El ancho viene en píxeles y se queda fijo mientras el contenedor de afuera se
      // abre o se cierra: así el contenido no se aplasta durante la animación.
      className="flex h-full shrink-0 flex-col overflow-hidden rounded-l-[26px] border-l border-[#02062B]/[0.06] bg-white"
      style={{ width: ancho }}
    >
      {cabecera}

      {alta ? (
        <AltaDeBiblioteca
          alta={alta}
          usuarioId={usuarioId}
          companyId={companyId}
          brandId={marcaId}
          plan={plan}
          rubro={rubro}
          subiendoPose={subiendoPose}
          onVolver={() => setAlta(null)}
          onProductosRefrescados={refrescarProductos}
          onModelosRefrescados={refrescarModelos}
          onModeloListo={(modelo) => enganchar(adjuntoDeModelo(modelo))}
          onFondosRefrescados={() => void refrescarFondosGuardados()}
          onFondoListo={(fondo) => enganchar(adjuntoDeFondoGuardado(fondo))}
          onFotoDePose={(archivo) => void subirPose(archivo)}
        />
      ) : (
        <>
          {/* Las pestañas: el azul clarito de las sugerencias marca la activa. Envuelven
              en dos filas si hace falta. */}
          <div role="tablist" aria-label="Qué adjuntar" className="flex flex-wrap gap-1.5 px-4 pt-3">
            {pestanas.map((p) => (
              <button
                key={p.id}
                type="button"
                role="tab"
                aria-selected={p.id === pestana}
                onClick={() => onCambiarPestana(p.id)}
                className={`rounded-full py-1.5 px-3 text-[13px] font-semibold transition-colors ${
                  p.id === pestana
                    ? 'bg-[#E9EDFD] text-brand-primary'
                    : 'bg-neutral-100 text-[#02062BE6] hover:bg-neutral-200'
                }`}
              >
                {p.etiqueta}
              </button>
            ))}
          </div>

          {conNombres && (
            <label className="mx-4 mt-3 flex items-center gap-2 rounded-xl bg-[#F0F2F7] px-3 py-2">
              <Search className="h-4 w-4 shrink-0 text-[#02062B]/[0.4]" aria-hidden="true" />
              <input
                type="search"
                value={busqueda}
                onChange={(e) => setBusqueda(e.target.value)}
                placeholder={`Buscar en ${pestanaActual.etiqueta.toLowerCase()}`}
                aria-label={`Buscar en ${pestanaActual.etiqueta.toLowerCase()}`}
                className="min-w-0 flex-1 bg-transparent text-[13.5px] text-brand-black outline-none placeholder:text-[#02062B]/[0.38]"
              />
            </label>
          )}

          {aviso && (
            <p role="status" className="mx-4 mt-2 text-[12.5px] text-[#C33B36]">
              {aviso}
            </p>
          )}

          {/* El input de "Subir imagen": vive acá, fuera de la grilla, porque lo dispara
              la tarjeta de alta. Sólo imágenes: lo demás va por el "+" de la caja. */}
          <input
            ref={inputImagenesRef}
            type="file"
            accept="image/*"
            multiple
            className="hidden"
            tabIndex={-1}
            aria-hidden="true"
            onChange={(e) => {
              const elegidos = Array.from(e.target.files ?? []);
              e.target.value = '';
              void subirImagenes(elegidos);
            }}
          />

          <div className="min-h-0 flex-1 overflow-y-auto px-4 pb-5 pt-4" style={{ scrollbarWidth: 'thin' }}>
            {pestana === 'moodboards' ? (
              <BibliotecaMoodboards
                companyId={companyId}
                brandId={marcaId}
                marcaResuelta={marcaResuelta}
                usuarioId={usuarioId}
                adjuntos={chat.adjuntos}
                onTomar={tomar}
                onCarpetasCambiadas={chat.refrescarMoodboards}
              />
            ) : cargando ? (
              <p className="py-8 text-center text-[13px] text-[#02062B]/[0.45]">Cargando…</p>
            ) : (
              <div ref={grillaRef} className="grid grid-cols-3 gap-x-3 gap-y-4">
                {/* Las altas, primero: la misma tarjeta de acción que encabeza las grillas
                    de los selectores. La de pose y la de imagen muestran el spinner
                    mientras sube, como en el sheet de poses. */}
                {altas.map((a) => (
                  <AccionCard
                    key={a.id}
                    className={`${aspecto} w-full`}
                    label={a.etiqueta}
                    sublabel={a.detalle}
                    icon={<Plus className="h-5 w-5" strokeWidth={2.5} />}
                    loading={
                      (a.id === 'subir-pose' && subiendoPose) ||
                      (a.id === 'subir-imagen' && subiendoImagenes)
                    }
                    loadingLabel={a.id === 'subir-pose' ? 'Creando la pose…' : 'Subiendo…'}
                    onClick={() => {
                      setAviso(null);
                      if (a.id === 'subir-imagen') inputImagenesRef.current?.click();
                      else setAlta(a.id);
                    }}
                  />
                ))}
                {visibles.map((f) => (
                    <button
                      key={f.clave}
                      type="button"
                      {...{ [ATRIBUTO_FICHA]: f.clave }}
                      onClick={(e) =>
                        tomar(f, e.currentTarget.querySelector<HTMLElement>('[data-miniatura]'))
                      }
                      title={f.nombre ?? undefined}
                      className="group flex min-w-0 flex-col gap-1.5 text-left"
                    >
                      <span
                        data-miniatura=""
                        className={`relative block ${aspecto} w-full overflow-hidden rounded-xl bg-[#F0F2F7] ring-2 ring-transparent ring-offset-2 transition-shadow group-hover:ring-[#02062B]/[0.12]`}
                      >
                        <CachedImg src={f.imageUrl} alt="" className="h-full w-full object-cover" />
                      </span>
                      {f.nombre && (
                        <span className="block truncate text-[12px] font-medium text-brand-black">
                          {f.nombre}
                        </span>
                      )}
                      <SkuDelProducto sku={f.sku} chico />
                    </button>
                ))}
                {visibles.length === 0 && altas.length === 0 && (
                  <p className="col-span-3 py-8 text-center text-[13px] text-[#02062B]/[0.45]">
                    {filtro
                      ? 'Nada con ese nombre.'
                      : fichas.length > 0
                        ? 'Ya está todo en el chat.'
                        : pestanaActual.vacio}
                  </p>
                )}
              </div>
            )}
            {!cargando && visibles.length === 0 && altas.length > 0 && (
              <p className="pt-4 text-center text-[13px] text-[#02062B]/[0.45]">
                {filtro
                  ? 'Nada con ese nombre.'
                  : fichas.length > 0
                    ? 'Ya está todo en el chat.'
                    : pestanaActual.vacio}
              </p>
            )}
          </div>
        </>
      )}
      {espejoDeAdjuntos && <CajaEspejoDeAdjuntos />}
    </aside>
  );
};

export default PanelBiblioteca;
